import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import { CircleVideoPlayer } from "@circle-react-shared/BuilderUI/Media/CircleVideoPlayer";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { ImagePlaceholder } from "./ImagePlaceholder";
import { defaultStyles } from "./defaultStyles";

export interface VideoUploadProps {
  customCoverImage?: {
    signed_id: string;
  };
  html?: string;
  isVideoLooped?: boolean;
  isVideoMuted?: boolean;
  signed_id: string;
  source?: string;
  videoAltText?: string;
}

interface VideoProps extends ComponentPropsWithRef<"div"> {
  "data-preview-mode"?: boolean;
  styles: Record<string, StyleValue>;
  videoUpload: VideoUploadProps;
}

export const Video = forwardRef<HTMLDivElement, VideoProps>(
  (
    { videoUpload, styles, "data-preview-mode": dataPreviewMode, ...rest },
    ref,
  ) => {
    const inlineStyles = {
      ...parseStyles({
        styles: omit(styles, "sizing"),
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
    };
    const {
      signed_id,
      customCoverImage,
      isVideoMuted,
      isVideoLooped,
      videoAltText,
      html,
    } = videoUpload || {};

    const attachmentMetadata = useAttachmentMetadata(signed_id);
    const coverImageAttachmentMetadata = useAttachmentMetadata(
      customCoverImage?.signed_id || "",
    );

    return (
      <div ref={ref} {...rest}>
        <CircleVideoPlayer
          url={attachmentMetadata?.url}
          html={html}
          isPreviewMode={dataPreviewMode}
          placeholder={<ImagePlaceholder />}
          isMuted={isVideoMuted}
          isLooped={isVideoLooped}
          description={videoAltText}
          poster={coverImageAttachmentMetadata?.url}
          styles={inlineStyles}
        />
      </div>
    );
  },
);

Video.displayName = "Video";
